import {HMDocument, UnpackedHypermediaId} from '@seed-hypermedia/client/hm-types'
import {hmId} from '@shm/shared'
import {IS_DESKTOP} from '@shm/shared/constants'
import {useResource} from '@shm/shared/models/entity'
import {useNavRoute} from '@shm/shared/utils/navigation'
import {useMemo} from 'react'
import {ScrollArea} from './components/scroll-area'
import {Feed} from './feed'
import {useDocumentLayout} from './layout'
import {MenuItemType, OptionsDropdown} from './options-dropdown'
import {PageLayout} from './page-layout'
import {CommentEditorProps, computeHeaderData, PageWrapper} from './resource-page-common'
import {Separator} from './separator'
import {Spinner} from './spinner'
import {Text} from './text'
import {useMedia} from './use-media'

export interface FeedPageProps {
  docId: UnpackedHypermediaId
  CommentEditor?: React.ComponentType<CommentEditorProps>
  extraMenuItems?: MenuItemType[]
  rightActions?: React.ReactNode
}

export function FeedPage({docId, extraMenuItems, rightActions}: FeedPageProps) {
  const siteHomeId = hmId(docId.uid)
  const siteHomeResource = useResource(siteHomeId, {subscribed: true})

  const siteHomeDocument: HMDocument | null =
    siteHomeResource.data?.type === 'document' ? siteHomeResource.data.document : null

  const headerData = computeHeaderData(siteHomeDocument)

  const targetDomain = siteHomeDocument?.metadata?.siteUrl || undefined

  if (siteHomeResource.isInitialLoading) {
    return (
      <PageWrapper
        siteHomeId={siteHomeId}
        docId={docId}
        headerData={headerData}
        isMainFeedVisible
        rightActions={rightActions}
      >
        <div className="flex flex-1 items-center justify-center">
          <Spinner />
        </div>
      </PageWrapper>
    )
  }

  return (
    <PageWrapper
      siteHomeId={siteHomeId}
      docId={docId}
      headerData={headerData}
      document={siteHomeDocument ?? undefined}
      isMainFeedVisible
      rightActions={rightActions}
    >
      <FeedBody siteHomeId={siteHomeId} extraMenuItems={extraMenuItems} targetDomain={targetDomain} />
    </PageWrapper>
  )
}

function FeedBody({
  siteHomeId,
  extraMenuItems,
  targetDomain,
}: {
  siteHomeId: UnpackedHypermediaId
  extraMenuItems?: MenuItemType[]
  targetDomain?: string
}) {
  const route = useNavRoute()

  const filterEventType = useMemo(() => {
    if (route.key === 'feed' && route.panel?.key === 'activity') {
      return (route.panel as any).filterEventType
    }
    return undefined
  }, [route])

  const {contentMaxWidth} = useDocumentLayout({
    contentWidth: undefined,
    showSidebars: false,
  })

  const media = useMedia()
  // In Electron (IS_DESKTOP), always use element scroll regardless of window width
  const isMobile = media.xs && !IS_DESKTOP

  const menuItems = extraMenuItems || []

  const feedContent = (
    <PageLayout contentMaxWidth={contentMaxWidth}>
      <div className="flex flex-col gap-4 pt-8">
        <div className="flex items-center justify-between px-4">
          <Text weight="bold" size="3xl">
            What's New
          </Text>
          {menuItems.length > 0 && <OptionsDropdown menuItems={menuItems} align="end" side="bottom" />}
        </div>
        <Separator />
        <Feed
          filterResource={`${siteHomeId.id}*`}
          targetDomain={targetDomain}
          size="md"
          filterEventType={filterEventType}
        />
      </div>
    </PageLayout>
  )

  if (isMobile) {
    return <div className="flex flex-1 flex-col pb-16">{feedContent}</div>
  }

  return (
    <div className="relative flex flex-1 flex-col overflow-hidden">
      <ScrollArea className="h-full">{feedContent}</ScrollArea>
    </div>
  )
}
